import { useTelemetry } from '@ctw/shared/context/telemetry/telemetry-boundary';
import { type ClassName, tw, twx } from '@ctw/shared/utils/tailwind';
import { faCheck } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

export interface TagCheckboxGroupOption {
  label: string;
  value: string;
}

export interface TagCheckboxGroupProps {
  legend: string;
  name: string;
  options: Readonly<Array<TagCheckboxGroupOption>>;
  selectedOptions: Array<string>;
  onOptionSelected: (option: TagCheckboxGroupOption) => void;
  onOptionDeselected: (option: TagCheckboxGroupOption) => void;
  className?: ClassName;
}

export const TagCheckboxGroup = ({
  legend,
  name,
  options,
  selectedOptions,
  onOptionSelected,
  onOptionDeselected,
  className,
}: TagCheckboxGroupProps) => {
  const telemetry = useTelemetry();

  return (
    <fieldset className={twx(className)}>
      <legend
        className={tw`mb-1 block font-medium text-content-subtle text-xs uppercase tracking-wide`}
      >
        {legend}
      </legend>
      <div className={tw`flex flex-wrap gap-2 transition-all duration-200`}>
        {options.map((option) => (
          <div
            key={option.value}
            className={twx('w-fit max-w-fit rounded transition-all duration-200', {
              'bg-background-disabled': !selectedOptions.includes(option.value),
              'bg-success-background': selectedOptions.includes(option.value),
            })}
          >
            <label
              className={tw`relative flex h-full w-full cursor-pointer items-center justify-center gap-1 px-2 py-1 transition-all duration-200`}
            >
              <input
                type="checkbox"
                name={name}
                value={option.value}
                checked={selectedOptions.includes(option.value)}
                className={tw`pointer-events-none absolute top-0 right-0 bottom-0 left-0 h-full w-full appearance-none border-0 bg-transparent transition-all duration-200`}
                onChange={(event) => {
                  if (event.target.checked) {
                    telemetry.trackInteraction('tag_checkbox_group_option_selected', {
                      tagCheckboxOptionValue: option.value,
                    });
                    onOptionSelected(option);
                  } else {
                    telemetry.trackInteraction('tag_checkbox_group_option_deselected', {
                      tagCheckboxOptionValue: option.value,
                    });
                    onOptionDeselected(option);
                  }
                }}
              />
              <span className={tw`font-medium text-content-main text-xs`}>{option.label}</span>
              <FontAwesomeIcon
                icon={faCheck}
                className={twx('h-3.5 text-success-main transition-all duration-200', {
                  'w-0 opacity-0': !selectedOptions.includes(option.value),
                  'w-3.5 opacity-100': selectedOptions.includes(option.value),
                })}
              />
            </label>
          </div>
        ))}
      </div>
    </fieldset>
  );
};
